/**
 * MFA (multi-factor authentication) client service — Phase B3, slice 2/3.
 *
 * Thin, well-typed wrappers over the Firebase Auth `multiFactor` API for the
 * personal-vault second factor. TOTP-first (an authenticator app), which avoids
 * the SMS/reCAPTCHA dependency that has bitten OrderUp before. The vault's API
 * gate (`requireSecondFactor`) is what ENFORCES MFA; this is the enrollment +
 * sign-in-challenge plumbing the UI drives.
 *
 * Nothing here runs until Firebase MFA (TOTP) is enabled on the project's
 * Identity Platform — calls will throw `auth/operation-not-allowed` until then.
 *
 * @see docs/orderup-drive/VAULT_MFA.md
 */

import {
  getMultiFactorResolver,
  multiFactor,
  TotpMultiFactorGenerator,
  type MultiFactorError,
  type MultiFactorInfo,
  type MultiFactorResolver,
  type TotpSecret,
  type UserCredential,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { getFirebaseAuth } from '../firebase/config';
import { createLogger } from '../utils/logger';
import { createApiClient } from './apiClient';

const logger = createLogger('MfaService');

const vaultApiClient = createApiClient('/api/v1/vault');
const authApiClient = createApiClient('/api/v1/auth');

/** The Firebase factor id for a TOTP (authenticator-app) second factor. */
export const TOTP_FACTOR_ID = TotpMultiFactorGenerator.FACTOR_ID;

/** What the enrollment UI needs to render a QR + manual-entry fallback. */
export interface TotpEnrollmentStart {
  /** Opaque secret handle — pass back to {@link finishTotpEnrollment}. */
  readonly secret: TotpSecret;
  /** `otpauth://…` URL to render as a QR code. */
  readonly qrCodeUrl: string;
  /** The shared secret (base32) for manual entry when a QR can't be scanned. */
  readonly sharedSecretKey: string;
}

/** A second factor enrolled on the account (for the manage/unenroll list). */
export interface EnrolledFactor {
  readonly uid: string;
  readonly displayName: string | null;
  readonly factorId: string;
  readonly enrollmentTime: string;
}

function requireUser() {
  const user = getFirebaseAuth().currentUser;
  if (!user) {
    throw new FirebaseError(
      'auth/no-current-user',
      'A signed-in user is required to manage MFA.',
    );
  }
  return user;
}

/**
 * Begin TOTP enrollment: open an MFA session and generate a secret. The UI
 * renders {@link TotpEnrollmentStart.qrCodeUrl} (or the shared key), the user
 * scans it into their authenticator, then calls {@link finishTotpEnrollment}
 * with the 6-digit code to prove possession.
 *
 * @param accountLabel  Shown in the authenticator app (typically the email).
 * @param issuer        App name shown in the authenticator (default OrderUp).
 */
export const startTotpEnrollment = async (
  accountLabel: string,
  issuer = 'OrderUp',
): Promise<TotpEnrollmentStart> => {
  const user = requireUser();
  const session = await multiFactor(user).getSession();
  const secret = await TotpMultiFactorGenerator.generateSecret(session);
  return {
    secret,
    qrCodeUrl: secret.generateQrCodeUrl(accountLabel, issuer),
    sharedSecretKey: secret.secretKey,
  };
};

/**
 * Finish TOTP enrollment: verify the one-time code against the secret and
 * enroll the factor. Throws `auth/invalid-verification-code` on a wrong code.
 */
export const finishTotpEnrollment = async (
  secret: TotpSecret,
  oneTimeCode: string,
  displayName = 'Authenticator app',
): Promise<void> => {
  const user = requireUser();
  const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
    secret,
    oneTimeCode.trim(),
  );
  await multiFactor(user).enroll(assertion, displayName);
  logger.info('TOTP second factor enrolled');
};

/** The factors currently enrolled on the signed-in account. */
export const listEnrolledFactors = (): EnrolledFactor[] => {
  const user = requireUser();
  return multiFactor(user).enrolledFactors.map((f: MultiFactorInfo) => ({
    uid: f.uid,
    displayName: f.displayName ?? null,
    factorId: f.factorId,
    enrollmentTime: f.enrollmentTime,
  }));
};

/** Remove an enrolled factor by its uid (or pass the MultiFactorInfo). */
export const unenrollFactor = async (factorUid: string): Promise<void> => {
  const user = requireUser();
  await multiFactor(user).unenroll(factorUid);
  logger.info('Second factor unenrolled');
};

/** Does the signed-in user have at least one enrolled second factor? */
export const hasEnrolledFactor = (): boolean =>
  listEnrolledFactors().length > 0;

/**
 * Wrap a caught error: returns a {@link MultiFactorResolver} when the error is
 * the `auth/multi-factor-auth-required` sign-in challenge, else null. The UI
 * inspects `resolver.hints` to pick a factor, then calls
 * {@link resolveTotpSignIn}.
 */
export const getMfaResolver = (
  error: unknown,
): MultiFactorResolver | null => {
  if (
    error instanceof FirebaseError &&
    error.code === 'auth/multi-factor-auth-required'
  ) {
    return getMultiFactorResolver(
      getFirebaseAuth(),
      error as MultiFactorError,
    );
  }
  return null;
};

/** Is this caught error the MFA sign-in challenge? */
export const isMfaChallenge = (error: unknown): boolean =>
  error instanceof FirebaseError &&
  error.code === 'auth/multi-factor-auth-required';

/**
 * Complete a sign-in that was interrupted by the MFA challenge, using a TOTP
 * code. Resolves the FIRST enrolled TOTP factor on the resolver.
 *
 * @throws when the resolver carries no TOTP factor, or the code is invalid.
 */
export const resolveTotpSignIn = async (
  resolver: MultiFactorResolver,
  oneTimeCode: string,
): Promise<UserCredential> => {
  const hint = resolver.hints.find((h) => h.factorId === TOTP_FACTOR_ID);
  if (!hint) {
    throw new FirebaseError(
      'auth/no-totp-factor',
      'No authenticator-app factor is enrolled for this account.',
    );
  }
  const assertion = TotpMultiFactorGenerator.assertionForSignIn(
    hint.uid,
    oneTimeCode.trim(),
  );
  return resolver.resolveSignIn(assertion);
};

/**
 * Mint a fresh set of single-use recovery codes for the signed-in user (vault
 * API). Returns the plaintext codes ONCE — store them somewhere safe.
 * Regenerating invalidates any prior set.
 */
export const requestRecoveryCodes = async (): Promise<string[]> => {
  const env = (await vaultApiClient.post('/mfa/recovery-codes', {})) as unknown as {
    data?: { codes?: string[] };
  };
  return env.data?.codes ?? [];
};

/**
 * Redeem a recovery code to clear a lost authenticator. Re-verifies the password
 * server-side, so this is safe to call from the locked-out login screen. On
 * success the user's second factors are removed — retry sign-in, then re-enrol.
 * Throws on an incorrect email / password / code.
 */
export const redeemRecoveryCode = async (
  email: string,
  password: string,
  code: string,
): Promise<void> => {
  await authApiClient.post('/mfa/recovery/redeem', {
    email,
    password,
    code: code.trim(),
  });
};

export const mfaService = {
  startTotpEnrollment,
  finishTotpEnrollment,
  listEnrolledFactors,
  unenrollFactor,
  hasEnrolledFactor,
  getMfaResolver,
  isMfaChallenge,
  resolveTotpSignIn,
  requestRecoveryCodes,
  redeemRecoveryCode,
  TOTP_FACTOR_ID,
} as const;
